import { Library as LibraryIcon, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { BrandWordmark } from '@/components/brand/BrandWordmark';
import { Button, Notice } from '@/components/ui';
import { createAndSaveProject, formatSavedAt } from '@/features/projects';
import { getProjectRepository, type ProjectSummary } from '@/repositories/projectRepository';
import styles from './HomePage.module.css';

export function HomePage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<readonly ProjectSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let active = true;
    getProjectRepository()
      .list()
      .then((list) => active && setProjects(list))
      .catch(
        (e: unknown) =>
          active &&
          setError(e instanceof Error ? e.message : 'Saved projects could not be listed.'),
      );
    return () => {
      active = false;
    };
  }, []);

  const create = async () => {
    setCreating(true);
    setError(null);
    try {
      const doc = await createAndSaveProject();
      void navigate(`/studio/${encodeURIComponent(doc.id)}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'A new project could not be created.');
      setCreating(false);
    }
  };

  const recent = projects?.slice(0, 6) ?? [];
  return (
    <div className={styles.page}>
      <section className={styles.hero}>
        <BrandWordmark variant="primary" />
        <h1 className={styles.headline}>
          Line art, arranged <em>for your nails</em>.
        </h1>
        <p className={styles.lede}>
          Choose original vector line art, arrange it on a nail, and print a tracing template at its
          real size — no account needed.
        </p>
        <div className={styles.actions}>
          <Button
            variant="primary"
            icon={<Plus />}
            onClick={() => void create()}
            disabled={creating}
          >
            {creating ? 'Creating…' : 'Create a design'}
          </Button>
          <Button icon={<LibraryIcon />} onClick={() => void navigate('/library')}>
            Browse designs
          </Button>
        </div>
        {error ? (
          <Notice tone="error" live>
            {error}
          </Notice>
        ) : null}
      </section>

      <section className={styles.section} aria-labelledby="recent-heading">
        <div className={styles.sectionHead}>
          <h2 id="recent-heading" className={styles.sectionTitle}>
            Recent projects
          </h2>
          {projects !== null && projects.length > 0 ? (
            <Link to="/projects">All projects</Link>
          ) : null}
        </div>
        {projects === null && !error ? <p className={styles.muted}>Loading…</p> : null}
        {projects !== null && recent.length === 0 ? (
          <p className={styles.muted}>
            No projects yet. “Create a design” starts one on a 14 × 22 mm example nail.
          </p>
        ) : null}
        {recent.length > 0 ? (
          <ul className={styles.projectList}>
            {recent.map((p) => (
              <li key={p.id}>
                <Link to={`/studio/${encodeURIComponent(p.id)}`} className={styles.projectCard}>
                  <span className={styles.projectName}>{p.name}</span>
                  <span className={styles.muted}>
                    {p.nailCount} {p.nailCount === 1 ? 'nail' : 'nails'} · saved{' '}
                    {formatSavedAt(p.updatedAt)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        ) : null}
      </section>

      <p className={styles.localNote}>
        Projects are saved in this browser on this device. Download a backup to keep a copy
        elsewhere.
      </p>
    </div>
  );
}
